import { supabase } from './supabase';
import { loadLocalTeam } from './localTeam';

const DEFERRED_KEY = '4cm_onboarding_deferred';

export type OnboardingStatusSource = 'local-team' | 'remote-team' | 'deferred' | 'missing';

export interface OnboardingStatus {
  needsOnboarding: boolean;
  source: OnboardingStatusSource;
  error?: string;
}

type RemoteTeamCheck = () => Promise<boolean>;

function storageGet(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function storageSet(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    // Ignore private-mode/storage failures; onboarding can still proceed.
  }
}

function storageRemove(key: string): void {
  try {
    localStorage.removeItem(key);
  } catch {
    // noop
  }
}

async function hasRemoteTeam(): Promise<boolean> {
  const { data, error } = await supabase
    .from('teams')
    .select('id')
    .limit(1)
    .maybeSingle();
  if (error) throw new Error(error.message);
  return Boolean(data);
}

export function markOnboardingDeferred(): void {
  storageSet(DEFERRED_KEY, 'true');
}

export function clearOnboardingDeferred(): void {
  storageRemove(DEFERRED_KEY);
}

export function isOnboardingDeferred(): boolean {
  return storageGet(DEFERRED_KEY) === 'true';
}

/**
 * Synchronous fast-path. Returns a status if local-only signals already
 * answer the question (existing users); returns undefined when a remote
 * check is needed (fresh install / new device).
 */
export function resolveOnboardingStatusSync(): OnboardingStatus | undefined {
  if (loadLocalTeam()) return { needsOnboarding: false, source: 'local-team' };
  if (isOnboardingDeferred()) return { needsOnboarding: false, source: 'deferred' };
  return undefined;
}

export async function resolveOnboardingStatus(
  remoteTeamCheck: RemoteTeamCheck = hasRemoteTeam,
): Promise<OnboardingStatus> {
  const sync = resolveOnboardingStatusSync();
  if (sync) return sync;

  try {
    return await remoteTeamCheck()
      ? { needsOnboarding: false, source: 'remote-team' }
      : { needsOnboarding: true, source: 'missing' };
  } catch (err) {
    return {
      needsOnboarding: true,
      source: 'missing',
      error: err instanceof Error ? err.message : String(err),
    };
  }
}
